import React from "react";
import {
  GoogleMap,
  useJsApiLoader,
  DirectionsRenderer,
  Marker,
} from "@react-google-maps/api";

const LIBRARIES = ["places", "geometry"];

function GMap({ originLat, originLng, destinationLat, destinationLng }) {
  const containerStyle = {
    width: "100%",
    height: "400px",
    borderRadius: "10px",
  };

  const zoom = 12;

  const origin = React.useMemo(() => ({
    lat: parseFloat(originLat) || 0,
    lng: parseFloat(originLng) || 0,
  }), [originLat, originLng]);

  const destination = React.useMemo(() => ({
    lat: parseFloat(destinationLat) || 0,
    lng: parseFloat(destinationLng) || 0,
  }), [destinationLat, destinationLng]);

  const { isLoaded, loadError } = useJsApiLoader({
    id: "muvrrs-admin-map-script",
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_KEY || "AIzaSyBUsEmil2a0A2Of_GB5-UhbiTFCxg_MEa0",
    libraries: LIBRARIES,
  });

  const [directions, setDirections] = React.useState(null);

  React.useEffect(() => {
    if (isLoaded && window.google && origin.lat !== 0 && destination.lat !== 0) {
      const directionsService = new window.google.maps.DirectionsService();
      directionsService.route(
        {
          origin: new window.google.maps.LatLng(origin.lat, origin.lng),
          destination: new window.google.maps.LatLng(destination.lat, destination.lng),
          travelMode: window.google.maps.TravelMode.DRIVING,
        },
        (result, status) => {
          if (status === window.google.maps.DirectionsStatus.OK) {
            setDirections(result);
          } else {
            console.error(`Directions request failed due to ${status}`);
          }
        }
      );
    }
  }, [isLoaded, origin.lat, origin.lng, destination.lat, destination.lng]);

  const onLoad = React.useCallback(function callback(map) {
    if (window.google && origin.lat !== 0 && destination.lat !== 0) {
      const bounds = new window.google.maps.LatLngBounds();
      bounds.extend(origin);
      bounds.extend(destination);
      map.fitBounds(bounds);
    }
  }, [origin.lat, origin.lng, destination.lat, destination.lng]);

  if (loadError) {
    return (
      <div className="d-flex align-items-center justify-content-center bg-light rounded-3" style={containerStyle}>
        <div className="text-center p-4">
          <i className="fas fa-exclamation-triangle text-warning fs-1 mb-3"></i>
          <p className="mb-0 fw-bold text-dark">Map Loading Error</p>
          <p className="small text-muted">Please check your internet connection or API key restrictions.</p>
        </div>
      </div>
    );
  }

  return isLoaded ? (
    <GoogleMap
      mapContainerStyle={containerStyle}
      center={origin.lat ? origin : { lat: 24.8607, lng: 67.0011 }}
      zoom={zoom}
      onLoad={onLoad}
      options={{
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: true,
      }}
    >
      {directions ? (
        <DirectionsRenderer
          directions={directions}
          options={{
            markerOptions: { clickable: true },
          }}
        />
      ) : (
        <>
          {origin.lat !== 0 && (
            <Marker
              position={origin}
              label="P"
              title="Pickup Location"
            />
          )}
          {destination.lat !== 0 && (
            <Marker
              position={destination}
              label="D"
              title="Drop-off Location"
            />
          )}
        </>
      )}
    </GoogleMap>
  ) : (
    <div className="d-flex align-items-center justify-content-center bg-light rounded-3" style={containerStyle}>
      <div className="spinner-border text-primary" role="status">
        <span className="visually-hidden">Loading Map...</span>
      </div>
    </div>
  );
}

export default React.memo(GMap);
